
import React, { useState, useRef } from 'react';
import { UploadStatus, UserDetails, SubmissionData } from '../types';
import { MAX_FILE_SIZE_MB, ALLOWED_EXTENSIONS } from '../constants';

interface UploadZoneProps {
  onSuccess: (data: SubmissionData) => void;
}

const UploadZone: React.FC<UploadZoneProps> = ({ onSuccess }) => {
  const [status, setStatus] = useState<UploadStatus>(UploadStatus.IDLE);
  const [userInfo, setUserInfo] = useState<UserDetails>({ name: '', phone: '', email: '' });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isFormValid = userInfo.name.trim().length > 0 && 
                      userInfo.phone.trim().length >= 10 && 
                      userInfo.email.includes('@');

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`File is too large. Max limit is ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }

    const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      setError(`Unsupported format. Use ${ALLOWED_EXTENSIONS.join(', ')}.`);
      return;
    }

    setError(null);
    setSelectedFile(file);
    setStatus(UploadStatus.USER_INFO);
  };

  const handleFinalSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile || !isFormValid) return;
    
    startUpload(selectedFile);
  };

  const startUpload = (file: File) => {
    setStatus(UploadStatus.UPLOADING);
    setProgress(0);

    let currentProgress = 0;
    const interval = setInterval(() => {
      currentProgress += Math.random() * 15;
      if (currentProgress >= 100) {
        currentProgress = 100;
        clearInterval(interval);
        finalizeSubmission(file);
      }
      setProgress(Math.min(currentProgress, 100));
    }, 80);
  };

  const finalizeSubmission = async (file: File) => {
    setStatus(UploadStatus.SUBMITTING);
    
    const submission: SubmissionData = {
      user: userInfo,
      fileName: file.name,
      timestamp: new Date().toISOString()
    };

    // Simulated API call with new brand name
    console.log("%c[API] POST /v1/submissions to dealmycar", "color: #3b82f6; font-weight: bold", submission);

    // Simulate analysis delay
    setTimeout(() => {
      setStatus(UploadStatus.SUCCESS);
      onSuccess(submission);
    }, 1500);
  };

  const resetSelection = () => {
    setSelectedFile(null);
    setStatus(UploadStatus.IDLE);
    setError(null);
  };

  return (
    <div className="max-w-3xl mx-auto px-6">
      <div className={`relative p-1 glass rounded-[40px] transition-all duration-700 ${
        status === UploadStatus.IDLE ? 'shadow-[0_0_50px_-12px_rgba(59,130,246,0.3)]' : 'shadow-2xl shadow-blue-500/10'
      }`}>
        {/* Neon Glow Border Effect */}
        <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-cyan-400 opacity-20 blur-[2px] rounded-[40px] -z-10 group-hover:opacity-40 transition-opacity"></div>
        
        <div className="bg-black/90 rounded-[39px] p-8 md:p-14 border border-white/5">
          {status === UploadStatus.IDLE && (
            <div 
              className="text-center space-y-10 animate-in fade-in zoom-in duration-500 cursor-pointer group"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="relative w-28 h-28 mx-auto">
                <div className="absolute inset-0 bg-blue-500/10 blur-2xl rounded-full group-hover:bg-blue-500/20 transition-all"></div>
                <div className="relative w-full h-full bg-white/5 rounded-[32px] flex items-center justify-center border border-white/10 group-hover:border-blue-500/40 transition-all transform group-hover:scale-110 group-hover:-rotate-3">
                  <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
              </div>

              <div>
                <h3 className="text-4xl font-display font-black mb-4 italic uppercase tracking-tighter">
                  BEAT YOUR PRICE <span className="text-blue-500">INSTANTLY</span>
                </h3>
                <p className="text-gray-400 max-w-sm mx-auto text-lg">
                  Drop your quotation here. We leverage direct factory incentives to slash dealer margins.
                </p>
              </div>

              <div className="space-y-4">
                <button 
                  className="px-12 py-6 bg-blue-600 text-white font-black uppercase tracking-widest rounded-2xl hover:bg-white hover:text-blue-600 transition-all shadow-2xl shadow-blue-600/20 text-lg transform group-hover:scale-105"
                >
                  NEGOTIATE MY QUOTE
                </button>
                <div className="flex justify-center items-center gap-6 pt-4 opacity-50">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                    <span className="text-[10px] uppercase font-bold tracking-widest">Secure Upload</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                    <span className="text-[10px] uppercase font-bold tracking-widest">AI Verification</span>
                  </div>
                </div>
              </div>

              <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                onChange={handleFileSelect}
                accept={ALLOWED_EXTENSIONS.join(',')}
              />
              <p className="text-[10px] text-gray-600 uppercase tracking-[0.2em] font-bold">PDF • XLSX • DOCX • JPEG (MAX 2MB)</p>
            </div>
          )}

          {status === UploadStatus.USER_INFO && selectedFile && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex items-center justify-between p-5 bg-blue-500/5 rounded-3xl border border-blue-500/20">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-base font-bold truncate max-w-[200px]">{selectedFile.name}</p>
                    <p className="text-[10px] text-blue-400 uppercase tracking-widest font-black">Ready for Analysis</p>
                  </div>
                </div>
                <button 
                  onClick={resetSelection}
                  className="text-gray-500 hover:text-white p-3 hover:bg-white/5 rounded-full transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="text-center">
                <h3 className="text-3xl font-display font-black mb-2 uppercase italic tracking-tighter">Where should we send your <span className="text-green-400">Lower Price</span>?</h3>
                <p className="text-gray-400 text-sm">We'll notify you the moment the showroom discount is beaten.</p>
              </div>
              
              <form onSubmit={handleFinalSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase font-bold text-gray-500 ml-1 tracking-widest">Full Name</label>
                    <input 
                      type="text" 
                      placeholder="e.g. Rahul Sharma"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-5 focus:outline-none focus:border-blue-500 transition-colors text-lg"
                      value={userInfo.name}
                      onChange={e => setUserInfo({...userInfo, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] uppercase font-bold text-gray-500 ml-1 tracking-widest">WhatsApp Number</label>
                    <input 
                      type="tel" 
                      placeholder="+91"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-5 focus:outline-none focus:border-blue-500 transition-colors text-lg"
                      value={userInfo.phone}
                      onChange={e => setUserInfo({...userInfo, phone: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] uppercase font-bold text-gray-500 ml-1 tracking-widest">Email Address</label>
                  <input 
                    type="email" 
                    placeholder="name@example.com"
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-5 focus:outline-none focus:border-blue-500 transition-colors text-lg"
                    value={userInfo.email}
                    onChange={e => setUserInfo({...userInfo, email: e.target.value})}
                    required
                  />
                </div>
                <button 
                  type="submit"
                  disabled={!isFormValid}
                  className={`w-full py-6 font-black uppercase tracking-widest rounded-2xl transition-all transform active:scale-95 shadow-2xl text-lg ${
                    isFormValid 
                    ? 'bg-blue-600 text-white hover:bg-blue-500 shadow-blue-600/30' 
                    : 'bg-white/5 text-gray-600 cursor-not-allowed border border-white/5'
                  }`}
                >
                  UNLOCK MY DISCOUNT
                </button>
              </form>
            </div>
          )}

          {(status === UploadStatus.UPLOADING || status === UploadStatus.SUBMITTING) && (
            <div className="text-center py-10 space-y-10 animate-in fade-in duration-300">
              <div className="relative w-40 h-40 mx-auto">
                {/* Rotating scanner element */}
                <div className="absolute inset-0 border-4 border-blue-500/20 border-dashed rounded-full animate-[spin_8s_linear_infinite]"></div>
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="80" cy="80" r="72" stroke="currentColor" strokeWidth="12" fill="transparent" className="text-white/5" />
                  <circle
                    cx="80" cy="80" r="72" stroke="currentColor" strokeWidth="12" fill="transparent"
                    strokeDasharray={452}
                    strokeDashoffset={452 - (452 * (status === UploadStatus.SUBMITTING ? 100 : progress)) / 100}
                    className="text-blue-500 transition-all duration-300 ease-out"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-4xl font-black font-display text-glow">
                    {status === UploadStatus.SUBMITTING ? '100%' : Math.round(progress) + '%'}
                  </span>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-3xl font-display font-black text-glow uppercase tracking-tight italic">
                  {status === UploadStatus.UPLOADING ? 'SECURELY SCANNING...' : 'ANALYZING MARGINS...'}
                </h3>
                <p className="text-gray-400 animate-pulse text-lg max-w-sm mx-auto">
                  {status === UploadStatus.UPLOADING 
                    ? 'Comparing your quote with our dealmycar network database' 
                    : 'Force-negotiating your final price with national suppliers'}
                </p>
              </div>
            </div>
          )}

          {status === UploadStatus.SUCCESS && (
            <div className="text-center space-y-10 py-10 animate-in fade-in zoom-in duration-700">
              <div className="w-32 h-32 bg-green-500/10 rounded-full flex items-center justify-center mx-auto border border-green-500/20">
                <svg className="w-16 h-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="space-y-6">
                <h3 className="text-5xl font-display font-black text-glow uppercase italic tracking-tighter">PRICE DESTROYED!</h3>
                <div className="max-w-md mx-auto">
                  <p className="text-gray-400 text-xl leading-relaxed">
                    Great news, <span className="text-white font-bold">{userInfo.name}</span>! Our dealmycar engine has identified extra margins in your dealer's quote.
                  </p>
                </div>
                <div className="inline-block p-1 rounded-2xl bg-gradient-to-r from-blue-600 to-cyan-500 shadow-xl shadow-blue-600/20 mt-6">
                   <div className="px-8 py-4 bg-black rounded-[14px]">
                      <p className="text-blue-400 font-black uppercase tracking-widest text-sm">
                        REPORT GENERATED BELOW
                      </p>
                   </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-8 p-5 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-4 text-red-400 animate-in shake duration-500">
              <svg className="w-6 h-6 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-bold text-sm uppercase tracking-wider">{error}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadZone;
